"use client"

import * as React from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { Button } from '@open-mercato/ui/primitives/button'
import { ErrorMessage, LoadingMessage } from '@open-mercato/ui/backend/detail'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { apiCall } from '@open-mercato/ui/backend/utils/apiCall'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import { useT } from '@open-mercato/shared/lib/i18n/context'

type SlotDetail = {
  id: string
  locationId: string
  location?: { id: string; name: string; code?: string | null } | null
  slotType: string
  startTime?: string | null
  endTime?: string | null
  cargoId?: string | null
  cargo?: { id: string; referenceNumber: string } | null
  status: string
  modeData?: Record<string, unknown> | null
}

export default function SlotDetailPage({ params }: { params: { id: string } }) {
  const t = useT()
  const router = useRouter()
  const slotId = params.id
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const { runMutation } = useGuardedMutation<Record<string, unknown>>({
    contextId: `slots:detail:${slotId}`,
  })
  const [slot, setSlot] = React.useState<SlotDetail | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  const loadSlot = React.useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await apiCall<SlotDetail>(`/api/freight/slots/${slotId}`)
      if (!response.ok || !response.result) {
        throw new Error(t('slots.pages.detail.loadError', 'Failed to load slot.'))
      }
      setSlot(response.result)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('slots.pages.detail.loadError', 'Failed to load slot.'))
    } finally {
      setIsLoading(false)
    }
  }, [slotId, t])

  React.useEffect(() => {
    void loadSlot()
  }, [loadSlot])

  const handleCancel = React.useCallback(async () => {
    if (!slot || slot.status !== 'BOOKED') return
    const confirmed = await confirm({
      title: t('slots.actions.cancel', 'Cancel slot'),
      text: t('slots.actions.cancelConfirm', 'Cancel this slot booking?'),
      variant: 'destructive',
    })
    if (!confirmed) return

    try {
      await runMutation({
        operation: async () => {
          const response = await apiCall<{ id?: string; status?: string }>(`/api/freight/slots/${slot.id}/cancel`, {
            method: 'PATCH',
          })
          if (!response.ok) {
            throw new Error(t('slots.pages.detail.cancelError', 'Failed to cancel slot.'))
          }
          return response
        },
        context: { entityType: 'freight:terminal_slot', entityId: slot.id },
        mutationPayload: { id: slot.id },
      })
      flash(t('slots.pages.detail.cancelSuccess', 'Slot cancelled.'), 'success')
      await loadSlot()
    } catch (cancelError) {
      console.error('slots.cancel', cancelError)
      flash(t('slots.pages.detail.cancelError', 'Failed to cancel slot.'), 'error')
    }
  }, [confirm, loadSlot, runMutation, slot, t])

  if (isLoading) {
    return (
      <Page>
        <PageBody>
          <LoadingMessage label={t('slots.pages.detail.loading', 'Loading slot…')} />
        </PageBody>
      </Page>
    )
  }

  if (error || !slot) {
    return (
      <Page>
        <PageBody>
          <ErrorMessage label={error ?? t('slots.pages.detail.loadError', 'Failed to load slot.')} />
        </PageBody>
      </Page>
    )
  }

  return (
    <Page>
      <PageBody className="space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">{t('slots.pages.detail.title', 'Slot detail')}</h1>
            <p className="text-sm text-muted-foreground">
              {slot.location?.name ?? slot.locationId}
            </p>
          </div>
          <div className="flex gap-2">
            {slot.status === 'BOOKED' ? (
              <Button type="button" variant="destructive" onClick={() => { void handleCancel() }}>
                {t('slots.actions.cancel', 'Cancel slot')}
              </Button>
            ) : null}
            <Button asChild variant="outline">
              <Link href="/backend/slots">{t('slots.actions.back', 'Back')}</Link>
            </Button>
          </div>
        </div>

        <div className="grid gap-4 rounded-lg border bg-background p-4 md:grid-cols-2">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('slots.table.location', 'Location')}
            </div>
            <div className="mt-1 text-sm">{slot.location?.name ?? slot.locationId}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('slots.table.slotType', 'Slot type')}
            </div>
            <div className="mt-1 text-sm">{t(`slots.types.${slot.slotType}`, slot.slotType)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('slots.table.startTime', 'Start time')}
            </div>
            <div className="mt-1 text-sm">{slot.startTime ? new Date(slot.startTime).toLocaleString() : '—'}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('slots.table.endTime', 'End time')}
            </div>
            <div className="mt-1 text-sm">{slot.endTime ? new Date(slot.endTime).toLocaleString() : '—'}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('slots.table.cargo', 'Cargo')}
            </div>
            <div className="mt-1 text-sm">{slot.cargo?.referenceNumber ?? t('slots.table.noCargo', 'No cargo')}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('slots.table.status', 'Status')}
            </div>
            <div className="mt-1 text-sm">{t(`slots.status.${slot.status}`, slot.status)}</div>
          </div>
        </div>
        {ConfirmDialogElement}
      </PageBody>
    </Page>
  )
}
